import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../services/api";
import { PublicVideoResponse } from "../../types/api";
import Spinner from "../Spinner";
import { FamflixMark, ClockIcon } from "../icons";
import InfoScreen from "../InfoScreen";
import VideoFrame from "./VideoFrame";
import { useI18n } from "../../i18n";

// The key is the human-readable name; drop the .mp4 extension for display.
const titleFromKey = (key: string): string => key.replace(/\.mp4$/i, "");

export default function PublicVideoPage() {
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();

  const [video, setVideo] = useState<PublicVideoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!slug) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    apiService
      .getPublicVideo(slug)
      .then((result) => {
        if (active) {
          setVideo(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [slug]);

  if (isLoading) {
    return <Spinner text={t("publicVideo.loading")} />;
  }

  if (hasError || !video) {
    return (
      <InfoScreen
        icon={<ClockIcon className="w-12 h-12 opacity-70" />}
        title={t("publicVideo.errorTitle")}
      >
        <p className="opacity-70">{t("publicVideo.errorBody")}</p>
      </InfoScreen>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center justify-center p-4 gap-4">
      <div className="w-full max-w-4xl">
        <div className="flex items-baseline justify-between mb-3">
          <span className="flex items-center gap-2 text-primary">
            <FamflixMark className="w-7 h-7" />
            <span className="text-xl font-bold text-base-content font-display tracking-tight">
              Famflix
            </span>
          </span>
        </div>

        <p className="text-base-content/80 mb-2">{titleFromKey(video.key)}</p>

        <VideoFrame>
          <video
            className="absolute top-0 left-0 w-full h-full"
            controls
            autoPlay
            src={video.url}
          />
        </VideoFrame>

        <p className="text-xs opacity-50 mt-3 text-center">
          {t("shared.footer")}
        </p>
      </div>
    </div>
  );
}
